import React, { useCallback } from 'react';
import { NextPage } from 'next';
import {
  Button, ButtonColor, Image, TypographyName,
} from '@doveit/lego';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import { importDynamicData } from '../services/importDynamicData';
import PageSection from '../client/components/page-section/PageSection';
import {
  City, CTA, HeroSection, TextAlign,
} from '../client/types';
import CitiesHList from '../client/components/cities-hlist/CitiesHList';
import { getHighlightedCities } from '../services/cities/cities';
import ListReviews from '../client/containers/list-reviews/ListReviews';
import { ReviewTarget } from '../domain/graphcms';
import LogoList from '../client/components/logo-list/LogoList';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import { LogosSectionProps } from '../client/templates/common/logos-section/LogosSection';
import Card from '../client/components/card/Card';
import * as styles from '../client/templates/auction-consultancy/AuctionConsultancy.style';
import HowItWorksSection, { HowItWorksSectionProps } from '../client/templates/common/how-it-works-section/HowItWorksSection';
import PackageCard, { PackageCardProps } from '../client/components/package-card/PackageCard';
import Hero from '../client/templates/common/hero/Hero';
import CreateConsultancy from '../client/containers/create-consultancy/CreateConsultancy';
import { ConsultancyType } from '../services/dtos/consultancy';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import {
  AUCTION_CONSULTANCY_THANK_YOU_AS, buildURLObject, THANK_YOU_URL,
} from '../client/utils/links';
import { sendEventAndRedirect } from '../client/utils/tracking';
import { getPartnersData } from '../services/partners/getPartnersData';
import getTrafficSource from '../client/utils/traffic-source/getTrafficSource';
import useRefCallback from '../client/hooks/useRefCallback/useRefCallback';
import { ThankYouPageVariant } from '../services/thank-you/getThankYouData';

export interface AuctionConsultancyProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection,
  whyChooseSection: {
    title: string,
    description: string,
  },
  howItWorksSection: HowItWorksSectionProps & { description?: string },
  servicesSection: {
    title: string,
    picture: {
      name: string,
      alt: string,
    },
    items: {
      title: string,
      description: string,
    }[],
  },
  priceSection: {
    title: string,
    packages: PackageCardProps[],
  },
  citiesSection: {
    title: string,
    subtitle: string,
    cities: City[],
    cta?: CTA,
  },
  infoSection: {
    title: string,
    subtitle: string,
    description: string,
  },
  press: LogosSectionProps & { cta?: CTA },
}

export const AuctionConsultancy: NextPage<AuctionConsultancyProps> = ({
  SEO,
  hero,
  whyChooseSection,
  howItWorksSection,
  servicesSection,
  citiesSection,
  priceSection,
  infoSection,
  press,
}) => {
  const { current, ref: formRef } = useRefCallback();

  const onSuccess = useCallback(() => {
    sendEventAndRedirect(
      'auction_consultancy_submit',
      {
        action: 'submit',
        category: 'auction-consultancy',
        label: 'hero',
      },
      {
        urlObject: buildURLObject(
          THANK_YOU_URL,
          AUCTION_CONSULTANCY_THANK_YOU_AS,
          { variant: ThankYouPageVariant.AUCTION_CONSULTANCY },
        ),
      },
    );
  }, []);

  return (
    <AppLayout>
      <PageHead SEO={SEO} />
      <PageLayout>
        <PageLayout.Header>
          <Hero
            title={hero.title}
            backgroundImage={requireResourcesFromStaticAssets(hero.backgroundImage)}
            description={hero.description}
            secondarySlotTitle={hero.formTitle}
            secondarySlot={(
              <div ref={formRef}>
                <CreateConsultancy
                  consultancyType={ConsultancyType.AUCTION}
                  dataTracking="auction-consultancy-form"
                  onSuccess={onSuccess}
                />
              </div>
            )}
          />
        </PageLayout.Header>
        <PageLayout.Content>
          <PageLayout.Main>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  textAlign={TextAlign.CENTER}
                  content={whyChooseSection.title}
                />
                <PageSection.Block>
                  <PageSection.Description
                    textAlign={TextAlign.CENTER}
                    text={whyChooseSection.description}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <HowItWorksSection
              title={howItWorksSection.title}
              picture={howItWorksSection.picture}
              steps={howItWorksSection.steps}
            />
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  content={servicesSection.title}
                />
                <PageSection.Block>
                  <div css={styles.services}>
                    <div css={styles.servicesBlock}>
                      <div css={styles.servicesList}>
                        {servicesSection.items.map((service) => (
                          <div
                            key={service.title}
                            css={styles.serviceItem}
                          >
                            <Card>
                              <Card.Title>
                                {service.title}
                              </Card.Title>
                              <Card.Text>
                                {service.description}
                              </Card.Text>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div css={styles.servicesImage}>
                      <Image
                        src={requireFromStaticAsset(servicesSection.picture.name)}
                        alt={servicesSection.picture.alt}
                      />
                    </div>
                  </div>
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  content={priceSection.title}
                  textAlign={TextAlign.CENTER}
                />
                <PageSection.Block>
                  <div css={styles.packageList}>
                    {priceSection.packages.map((item) => (
                      <div
                        key={item.title}
                        css={styles.packageItem}
                      >
                        <PackageCard
                          {...item}
                          cta={{
                            ...item.cta,
                            onClick: scrollToRef(current),
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title textAlign={TextAlign.CENTER}>
                  {citiesSection.title}
                </PageSection.Title>
                {citiesSection.subtitle && (
                  <PageSection.Description
                    textAlign={TextAlign.CENTER}
                    size={TypographyName.H4}
                    color="neutrals.dark"
                    text={citiesSection.subtitle}
                  />
                )}
              </PageSection.Wrapper>
              <PageSection.Wrapper withoutPad>
                <PageSection.Block>
                  <CitiesHList cities={citiesSection.cities} />
                </PageSection.Block>
              </PageSection.Wrapper>
              {citiesSection.cta && (
                <PageSection.Wrapper>
                  <PageSection.Actions textAlign={TextAlign.CENTER}>
                    <Button
                      {...citiesSection.cta}
                      color={ButtonColor.SECONDARY}
                      onClick={scrollToRef(current)}
                    />
                  </PageSection.Actions>
                </PageSection.Wrapper>
              )}
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  textAlign={TextAlign.CENTER}
                  content={infoSection.title}
                />
                <PageSection.Description
                  textAlign={TextAlign.CENTER}
                  text={infoSection.subtitle}
                  size={TypographyName.H4}
                  color="neutrals.dark"
                />
                <PageSection.Block>
                  <PageSection.Description
                    textAlign={TextAlign.CENTER}
                    text={infoSection.description}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  textAlign={TextAlign.CENTER}
                  content="Le recensioni dei nostri clienti su Trustpilot"
                />
                <PageSection.Description
                  textAlign={TextAlign.CENTER}
                  text="Il 98% dei nostri clienti ha giudicato il servizio <strong>ECCELLENTE</strong>"
                  size={TypographyName.H4}
                  color="neutrals.dark"
                />
              </PageSection.Wrapper>
              <PageSection.Wrapper withoutPad>
                <PageSection.Block>
                  <ListReviews
                    target={[ReviewTarget.COMPRATORE]}
                    showLink
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  textAlign={TextAlign.CENTER}
                  content={press.title}
                />
                <PageSection.Block>
                  <LogoList
                    items={press.items
                      .map((item) => ({
                        ...item,
                        picture: requireFromStaticAsset(item.picture),
                      }))}
                    inlineItems={6}
                  />
                </PageSection.Block>
                {press.cta && (
                  <PageSection.Actions textAlign={TextAlign.CENTER}>
                    <Button
                      {...press.cta}
                      color={ButtonColor.SECONDARY}
                      onClick={scrollToRef(current)}
                    />
                  </PageSection.Actions>
                )}
              </PageSection.Wrapper>
            </PageSection>
          </PageLayout.Main>
        </PageLayout.Content>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError(async ({ query }) => {
  const [pageData, overridePageData, cities, press] = await Promise.all([
    importDynamicData('pages/auction-consultancy.yml'),
    getPartnersData(getTrafficSource(query), 'auction-consultancy.yml'),
    getHighlightedCities(),
    importDynamicData('press.yml'),
  ]);

  const mergedPageData = { ...pageData, ...overridePageData };

  return {
    props: {
      ...mergedPageData,
      citiesSection: {
        cities,
        ...mergedPageData.citiesSection,
      },
      press: {
        ...mergedPageData.press,
        items: press.items,
      },
    },
  };
});

export default withError(AuctionConsultancy);
